import { CommandProps, JSONContent, Node, mergeAttributes } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';

import { DEFAULT_TIPTAP_PARSE_HTML_PRIORITY, TiptapNodeType } from '../../tiptapTypes';
import { HIDDEN_TASK_LIST_TITLE_ATTR } from './TaskListDocument';
import { handleEmptyTitleBackspace, handleTitleEnter } from './taskListTitleKeyboardHandlers';
import { createTitleToTaskInputRule } from './taskListTitleInputRules';
import { createTaskListTitlePasteConversionPlugin } from './taskListTitlePasteConversionPlugin';
import { editorStateHasTaskListTitle } from './taskListTitleUtils';

declare module '@tiptap/core' {
    // oxlint-disable-next-line typescript/consistent-type-definitions -- augmenting an interface needs declaration merging
    interface Commands<ReturnType> {
        taskListTitle: {
            /**
             * Adds an empty task list title node as the first document node.
             */
            addTaskListTitle: () => ReturnType;
            /**
             * Removes the visible task list title node.
             */
            removeTaskListTitle: () => ReturnType;
            /**
             * Adds or removes the visible task list title node.
             */
            toggleTaskListTitle: () => ReturnType;
        };
    }
}

/**
 * Creates the title node to insert, preferring any previously hidden title content.
 */
const createTaskListTitleNode = (state: CommandProps['state'], titleTypeName: string) => {
    const hiddenTitle = state.doc.attrs[HIDDEN_TASK_LIST_TITLE_ATTR] as JSONContent | null;

    if (hiddenTitle?.type === titleTypeName) return state.schema.nodeFromJSON(hiddenTitle);

    return state.schema.nodes[titleTypeName]?.createAndFill();
};

/**
 * The optional title shown as the first line of the single task list editor.
 *
 * Modelled as a dedicated node (rather than a reused paragraph) so the schema itself
 * enforces the constraints we want:
 *   - position: the document content model (`taskListTitle? taskList`) only allows it as
 *     the first child, and at most once.
 *   - no styling: `marks: ''` disallows all inline marks (bold/italic/link/etc.) —
 *     the title is plain text only.
 *   - soft line breaks: `hardBreak` is allowed in the content, so Shift-Enter (bound by
 *     the StarterKit HardBreak extension) inserts a soft break within the single title node.
 *     (Enter/arrow keyboard behaviour is layered on separately.)
 */
export const TaskListTitle = Node.create({
    name: TiptapNodeType.taskListTitle,

    content: '(text | hardBreak)*',

    // Disallow all inline marks so the title can never carry styling.
    marks: '',

    group: 'title',

    // Treat as a defining context so operations like paste replace its content
    // rather than splitting it into a different node type.
    defining: true,

    parseHTML() {
        // Outrank a generic `h2` rule, so a schema holding both parses the title as a title.
        return [{ tag: 'h2[data-type="task-list-title"]', priority: DEFAULT_TIPTAP_PARSE_HTML_PRIORITY + 1 }];
    },

    renderHTML({ HTMLAttributes }) {
        return ['h2', mergeAttributes(HTMLAttributes, { 'data-type': 'task-list-title' }), 0];
    },

    addCommands() {
        return {
            addTaskListTitle:
                () =>
                ({ state, dispatch }) => {
                    if (state.doc.firstChild?.type.name === this.name) return false;

                    const titleNode = createTaskListTitleNode(state, this.name);

                    if (!titleNode) return false;

                    const transaction = state.tr
                        .setDocAttribute(HIDDEN_TASK_LIST_TITLE_ATTR, null)
                        .insert(0, titleNode);
                    transaction.setSelection(TextSelection.near(transaction.doc.resolve(titleNode.nodeSize - 1), -1));
                    dispatch?.(transaction);

                    return true;
                },
            removeTaskListTitle:
                () =>
                ({ state, dispatch }) => {
                    const titleNode = state.doc.firstChild;

                    if (titleNode?.type.name !== this.name) return false;

                    const hiddenTitle = titleNode.content.size > 0 ? titleNode.toJSON() : null;
                    const transaction = state.tr
                        .setDocAttribute(HIDDEN_TASK_LIST_TITLE_ATTR, hiddenTitle)
                        .delete(0, titleNode.nodeSize);
                    transaction.setSelection(TextSelection.near(transaction.doc.resolve(0), 1));
                    dispatch?.(transaction);

                    return true;
                },
            toggleTaskListTitle:
                () =>
                ({ commands, state }) =>
                    editorStateHasTaskListTitle(state) ? commands.removeTaskListTitle() : commands.addTaskListTitle(),
        };
    },

    addKeyboardShortcuts() {
        const handleBackspace = () =>
            this.editor.commands.command((props) => handleEmptyTitleBackspace(props, this.name));

        return {
            Backspace: handleBackspace,
            // Tiptap's own Backspace handling converts an empty first block to the default type, which the document refuses
            'Mod-Backspace': handleBackspace,
            'Alt-Backspace': handleBackspace,
            'Shift-Backspace': handleBackspace,
            Enter: () => this.editor.commands.command((props) => handleTitleEnter(props, this.name)),
        };
    },

    addInputRules() {
        return [createTitleToTaskInputRule(this.name)];
    },

    addProseMirrorPlugins() {
        return [
            // Pastes a copied list's title as a task, rather than as a row the copy nests under
            createTaskListTitlePasteConversionPlugin(),
        ];
    },
});
